import React, { useState } from 'react';
import { MinusIcon, PlusIcon } from 'lucide-react';
import type { Product, VariantSelection } from '../../types/product';
import { ProductGallery } from './ProductGallery';
import { VariantSelector } from './VariantSelector';
import { StarRating } from './StarRating';
import { StockLabel } from './StockLabel';
import { ProductPerks } from './ProductPerks';
import { StickyBuyBar } from './StickyBuyBar';
import { FrequentlyBought } from './FrequentlyBought';
import { ProductTabs } from './ProductTabs';
import { RelatedProducts } from './RelatedProducts';
import { Breadcrumbs } from '../layout/Breadcrumbs';
import { useCart } from '../../contexts/CartContext';
import { useInView } from '../../hooks/useInView';
import { categories } from '../../data/categories';
import { formatPrice } from '../../utils/currency';
import { defaultSelection, discountPercent, resolveVariant } from '../../utils/products';
export function StandardProductView({
  product
}: {
  product: Product;
}) {
  const {
    addItem
  } = useCart();
  const [selection, setSelection] = useState<VariantSelection>(() => defaultSelection(product));
  const [qty, setQty] = useState(1);
  const {
    ref: buyRef,
    inView
  } = useInView<HTMLDivElement>('-120px 0px 0px 0px');
  const v = resolveVariant(product, selection);
  const category = categories.find(c => c.id === product.category);
  const discount = discountPercent(product);
  const cta = product.stock === 'preorder' ? 'Pre-order' : 'Add to cart';
  const add = () => addItem(product, selection, qty);
  return <div className="mx-auto max-w-7xl px-4 pb-24 pt-5 sm:px-6 lg:px-8" data-id="element-395" data-mp="1sp70jr6gkgim">
      <Breadcrumbs items={[{
      label: 'Home',
      href: '/'
    }, {
      label: category?.name ?? 'Shop',
      href: `/shop/${product.category}`
    }, {
      label: product.name
    }]} data-id="element-396" data-mp="1nv2qv05t5udu" />

      <div className="mt-5 grid gap-8 lg:grid-cols-12 lg:gap-12" data-id="element-397" data-mp="2gdzop1gu9k2o">
        <div className="lg:col-span-7" data-id="element-398" data-mp="2bkx5nn9ott0q">
          <div className="lg:sticky lg:top-40" data-id="element-399" data-mp="2bjvgkevu7600">
            <ProductGallery images={product.gallery} alt={product.name} activeImage={v.image} badges={<>
                  {discount > 0 && <span className="rounded-md bg-sale px-2 py-1 text-xs font-bold text-white" data-id="element-401" data-mp="3w4sjd5hhz20a">−{discount}%</span>}
                  {product.badge && <span className={`rounded-md px-2 py-1 text-xs font-bold ${product.badge === 'New' ? 'bg-volt text-ink' : 'bg-ink text-white'}`} data-id="element-402" data-mp="4vlgvb1fkufr">
                      {product.badge}
                    </span>}
                </>} data-id="element-400" data-mp="9o641tpu09ab" />
          </div>
        </div>

        <div className="lg:col-span-5" data-id="element-403" data-mp="3d7hkvn3owclw">
          <p className="text-sm font-medium text-muted" data-id="element-404" data-mp="3i3g651tq1ei3">{product.brand}</p>
          <h1 className="mt-1 text-2xl font-bold leading-tight tracking-tight text-ink sm:text-[32px]" data-id="element-405" data-mp="1be1i5rubv7ak">{product.name}</h1>
          <a href="#details" className="mt-3 inline-flex items-center gap-2 text-sm" data-id="element-406" data-mp="1gd4kh4ixazeu">
            <StarRating rating={product.rating} data-id="element-407" data-mp="1qg9zo6y97ovr" />
            <span className="text-muted underline-offset-2 hover:underline" data-id="element-408" data-mp="1qh1isianzu55">
              {product.rating} · {product.reviews.toLocaleString('en-IE')} reviews
            </span>
          </a>

          <ul className="mt-5 flex flex-wrap gap-1.5" data-id="element-409" data-mp="1zaeko9qpi63d">
            {product.specs.map(s => <li key={s} className="rounded-md bg-surface px-2.5 py-1.5 text-xs font-semibold text-ink" data-id="element-410" data-mp="1uh3msy3vgznl">
                {s}
              </li>)}
          </ul>

          <div className="mt-6 flex items-baseline gap-3" data-id="element-411" data-mp="247514y6fb3pb">
            <span className="text-[34px] font-bold leading-none tracking-tight text-ink" data-id="element-412" data-mp="2i7z7zcdtowlv">{formatPrice(v.price)}</span>
            {v.compareAt && <span className="text-base text-muted line-through" data-id="element-413" data-mp="2e7fecisgkb4h">{formatPrice(v.compareAt)}</span>}
          </div>
          <p className="mt-1.5 text-xs text-muted" data-id="element-414" data-mp="nupyvlp6vd45">
            Incl. VAT · or 3 × {formatPrice(Math.round(v.price / 3 * 100) / 100)} interest-free with Klarna
          </p>

          {product.variants && product.variants.length > 0 && <div className="mt-6 space-y-5 border-t border-line pt-6" data-id="element-415" data-mp="lbuzvh5rb0ga">
              {product.variants.map(g => <VariantSelector key={g.id} group={g} value={selection[g.id]} onChange={id => setSelection(s => ({
            ...s,
            [g.id]: id
          }))} data-id="element-416" data-mp="uagej183xvbs" />)}
            </div>}

          <div ref={buyRef} className="mt-6 flex gap-3" data-id="element-417" data-mp="2j0osyb97shc">
            <div className="flex h-12 items-center rounded-lg border border-line" data-id="element-418" data-mp="2klo118ijzlj">
              <button type="button" onClick={() => setQty(q => Math.max(1, q - 1))} aria-label="Decrease quantity" className="flex h-full w-11 items-center justify-center text-ink hover:bg-surface" data-id="element-419" data-mp="6lf9zp2eb4bs">
                <MinusIcon className="h-4 w-4" data-id="element-420" data-mp="3f17c84w2j7wr" />
              </button>
              <span className="w-8 text-center text-sm font-semibold tabular-nums" aria-live="polite" data-id="element-421" data-mp="3jzimuhu6tmg1">
                {qty}
              </span>
              <button type="button" onClick={() => setQty(q => q + 1)} aria-label="Increase quantity" className="flex h-full w-11 items-center justify-center text-ink hover:bg-surface" data-id="element-422" data-mp="3ceok32b3saup">
                <PlusIcon className="h-4 w-4" data-id="element-423" data-mp="2ygrj7tpm9ia8" />
              </button>
            </div>
            <button type="button" onClick={add} className="h-12 flex-1 rounded-lg bg-volt text-sm font-bold text-ink transition-[background-color,transform] duration-150 hover:bg-volt-hover active:scale-[0.98]" data-id="element-424" data-mp="2tgvf7ugx5quo">
              {cta}
            </button>
          </div>
          <div className="mt-3" data-id="element-425" data-mp="2et0nagaqjqin">
            <StockLabel status={product.stock} long data-id="element-426" data-mp="2etttng5s8e09" />
          </div>

          <div className="mt-6" data-id="element-427" data-mp="2apm4xizbqy21">
            <ProductPerks data-id="element-428" data-mp="2apuhf9rjc1cp" />
          </div>
        </div>
      </div>

      <div className="mt-14 space-y-12" data-id="element-429" data-mp="25sm01be7o1ci">
        <FrequentlyBought product={product} selection={selection} mainPrice={v.price} mainImage={v.image} data-id="element-430" data-mp="25tnku7l1427f" />
        <ProductTabs product={product} data-id="element-431" data-mp="1w6ba4w17l669" />
      </div>

      <RelatedProducts product={product} data-id="element-432" data-mp="206bo6fkebfcs" />

      <StickyBuyBar visible={!inView} image={v.image} name={product.name} options={product.variants ? v.labels : []} price={v.price} compareAt={v.compareAt} ctaLabel={cta} onAdd={add} data-id="element-433" data-mp="2037e51k91pe7" />
    </div>;
}